import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import axios from "axios";
import emailjs from "@emailjs/browser";
import { Toaster, toast } from "sonner";
import { motion, useScroll, useTransform, useInView, useSpring, AnimatePresence } from "framer-motion";
import CookiePolicy from "./CookiePolicy";
import PrivacyPolicy from "./PrivacyPolicy";
import { 
  House, 
  Wrench, 
  Buildings, 
  PaintBrush,
  Wall,
  Shield,
  Users,
  Clock,
  FileText,
  Trophy,
  Camera,
  Phone,
  Envelope,
  MapPin,
  List,
  X,
  ArrowRight,
  CaretRight,
  Quotes,
  CheckCircle,
  Star,
  Ruler,
  CurrencyCircleDollar,
  CalendarCheck,
  Handshake,
  HardHat,
  Target,
  WhatsappLogo,
  PhoneCall,
  Calculator,
  ChatCircleDots,
  CaretUp
} from "@phosphor-icons/react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Images
const IMAGES = {
  logo: "/logo.png",
  logoLight: "/logo.png",
  hero: "/ref4.jpg",
  portfolio1: "/ref2.jpg",
  portfolio2: "/ref1.jpg",
  portfolio3: "/ref3.jpg",
  portfolio4: "/ref5.jpg"
};

// Animated Counter Component
const AnimatedCounter = ({ value, suffix = "", prefix = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const spring = useSpring(0, { mass: 0.8, stiffness: 75, damping: 15 });
  const display = useTransform(spring, (current) => Math.round(current));
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (isInView) {
      spring.set(parseInt(value));
    }
  }, [isInView, spring, value]);

  useEffect(() => {
    const unsubscribe = display.on("change", (latest) => {
      setDisplayValue(latest);
    });
    return unsubscribe;
  }, [display]);

  return (
    <span ref={ref} className="stat-number-green">
      {prefix}{displayValue}{suffix}
    </span>
  );
};

// Section Reveal Animation
const SectionReveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8, delay, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {children}
    </motion.div>
  );
};

// Scroll Progress Bar
const ScrollProgressBar = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });
  
  return (
    <motion.div
      className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-400 via-teal-500 to-sky-500 origin-left z-[60]"
      style={{ scaleX }}
      data-testid="scroll-progress-bar"
    />
  );
};

// Floating Action Buttons (Smart Functions)
const FloatingActions = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={scrollToTop}
            className="fixed bottom-4 right-4 z-40 w-10 h-10 md:w-12 md:h-12 bg-cyan-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-cyan-700 transition-colors"
            data-testid="scroll-to-top"
          >
            <CaretUp size={20} weight="bold" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Pulsing Call Button */}
      <a
        href="tel:+36702602689"
        className="call-btn-pulse fixed bottom-4 left-4 z-40"
        data-testid="call-button"
      >
        <span className="call-btn-ring" />
        <span className="call-btn-ring call-btn-ring-2" />
        <span className="call-btn-inner">
          <Phone size={22} weight="fill" />
        </span>
        <span className="call-btn-label">Hívjon most!</span>
      </a>

      {/* Quick Calculator Floating Button - hidden on small mobile */}
      <motion.a
        href="#ajanlatkerem"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-16 md:bottom-20 left-4 z-40 w-10 h-10 md:w-14 md:h-14 bg-cyan-500 text-white rounded-full shadow-lg hidden sm:flex items-center justify-center hover:bg-cyan-600 transition-colors"
        data-testid="quick-calculator"
      >
        <Calculator size={20} />
      </motion.a>
    </>
  );
};

// Live Project Counter Widget
const LiveProjectCounter = () => {
  const [activeProjects, setActiveProjects] = useState(7);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Simulate live updates
  useEffect(() => {
    const interval = setInterval(() => {
      const variation = Math.random() > 0.7 ? (Math.random() > 0.5 ? 1 : -1) : 0;
      setActiveProjects(prev => Math.max(3, Math.min(15, prev + variation)));
      setLastUpdate(new Date());
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="inline-flex items-center gap-3 bg-cyan-50 border border-cyan-200 rounded-full px-4 py-2">
      <div className="flex items-center gap-2">
        <span className="relative flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-cyan-500"></span>
        </span>
        <span className="text-sm font-semibold text-cyan-700">{activeProjects} aktív projekt</span>
      </div>
    </div>
  );
};

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#szolgaltatasok", label: "Szolgáltatások" },
    { href: "#rolunk", label: "Rólunk" },
    { href: "#referenciak", label: "Referenciák" },
    { href: "#folyamat", label: "Folyamat" },
    { href: "#kapcsolat", label: "Kapcsolat" },
  ];

  return (
    <>
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? "glass-light shadow-lg py-3" : "bg-transparent py-5"
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="container-premium flex items-center justify-between">
          <a href="#hero" className="flex items-center">
            <img src={IMAGES.logoLight} alt="Báza-Bau Kft." className="h-12 sm:h-16 md:h-20 lg:h-24 w-auto" />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-10">
            {navLinks.map((link, index) => (
              <motion.a
                key={link.href}
                href={link.href}
                className="nav-link-eco"
                data-testid={`nav-link-${index}`}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 * index }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
              >
                {link.label}
              </motion.a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <a href="#ajanlatkerem">
              <Button className="btn-green" data-testid="nav-cta-button">
                Ajánlatkérés
                <ArrowRight weight="bold" className="ml-2" size={18} />
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-cyan-800"
            onClick={() => setMobileMenuOpen(true)}
            data-testid="mobile-menu-button"
          >
            <List size={28} weight="bold" />
          </button>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <div 
        className={`mobile-menu-overlay ${mobileMenuOpen ? "active" : ""}`}
        onClick={() => setMobileMenuOpen(false)}
      />
      <div className={`mobile-menu-panel ${mobileMenuOpen ? "active" : ""}`}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-10">
            <img src={IMAGES.logoLight} alt="Báza-Bau" className="h-16" />
            <button 
              onClick={() => setMobileMenuOpen(false)}
              className="text-cyan-800 p-2"
              data-testid="close-mobile-menu"
            >
              <X size={28} weight="bold" />
            </button>
          </div>
          <nav className="flex flex-col gap-6">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-cyan-800 text-xl font-medium border-b border-cyan-100 pb-4"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a href="#ajanlatkerem" onClick={() => setMobileMenuOpen(false)}>
              <Button className="btn-green w-full mt-4" data-testid="mobile-cta-button">
                Ajánlatkérés
              </Button>
            </a>
          </nav>
        </div>
      </div>
    </>
  );
};

// Hero Section - Eco Friendly
const HeroSection = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);

  return (
    <section id="hero" className="hero-eco relative">
      <motion.div 
        className="hero-bg-image-eco"
        style={{ 
          backgroundImage: `url('${IMAGES.hero}')`,
          y 
        }}
      />
      <div className="hero-overlay-eco" />
      
      {/* Decorative elements - hidden on mobile */}
      <div className="absolute top-32 right-20 opacity-20 animate-float hidden md:block">
        <House size={80} weight="duotone" className="text-cyan-500" />
      </div>
      <div className="absolute bottom-40 left-10 opacity-15 animate-float hidden md:block" style={{ animationDelay: '1s' }}>
        <Buildings size={100} weight="duotone" className="text-cyan-600" />
      </div>
      
      <div className="container-premium relative z-10 py-24 md:py-32 lg:py-0">
        <motion.div 
          className="max-w-3xl"
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-wrap items-center gap-4 mb-6"
          >
            <div className="eco-badge">
              <HardHat size={18} weight="fill" />
              Generálkivitelezés & Felújítás
            </div>
            <LiveProjectCounter />
          </motion.div>
          
          <motion.h1
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-cyan-900 mb-6 md:mb-8 leading-[1.1]"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Építünk, amiben<br />
            <span className="text-gradient-green">élni jó</span>
          </motion.h1>

          <motion.p
            className="text-base md:text-lg lg:text-xl text-gray-600 mb-8 md:mb-10 max-w-lg leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Prémium kivitelezés családoknak. Házépítés, felújítás, bővítés — 
            határidőre, fix áron, kulcsrakész állapotban.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <a href="#ajanlatkerem">
              <Button className="btn-green text-base" data-testid="hero-cta-button">
                Ingyenes Ajánlatkérés
                <ArrowRight weight="bold" className="ml-2" size={20} />
              </Button>
            </a>
            <a href="#referenciak">
              <Button className="btn-green-outline text-base" data-testid="hero-secondary-button">
                Referenciáink
              </Button>
            </a>
          </motion.div>
          
          {/* Feature Pills */}
          <motion.div
            className="flex flex-wrap gap-2 md:gap-3 mt-8 md:mt-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            {[
              { icon: <Shield size={14} />, text: "5 év garancia" },
              { icon: <Clock size={14} />, text: "Kötbéres határidő" },
              { icon: <Users size={14} />, text: "32 fős saját csapat" },
            ].map((item, idx) => (
              <span key={idx} className="flex items-center gap-1.5 text-xs md:text-sm text-cyan-700 bg-white/80 px-3 py-1.5 md:px-4 md:py-2 rounded-full border border-cyan-200">
                {item.icon}
                {item.text}
              </span>
            ))}
          </motion.div>
        </motion.div>

        {/* Remove floating card from here - it was overlapping with text */}
      </div>

      {/* Scroll Indicator */}
      <motion.div 
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2 hidden lg:block"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
      >
        <div className="text-xs text-cyan-600 mb-2 tracking-widest uppercase text-center">Görgessen</div>
        <div className="scroll-indicator-line-green" />
      </motion.div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const stats = [
    { value: "347", suffix: "+", label: "Sikeres Projekt", sublabel: "családi ház & felújítás", icon: <House size={28} weight="duotone" />, accent: "from-cyan-400 to-sky-400" },
    { value: "18", suffix: "K m²", label: "Kivitelezett Terület", sublabel: "ház, lakás, tető", icon: <Ruler size={28} weight="duotone" />, accent: "from-teal-400 to-cyan-400" },
    { value: "32", suffix: " fő", label: "Saját Szakember", sublabel: "nem alvállalkozó", icon: <Users size={28} weight="duotone" />, accent: "from-sky-400 to-teal-400" },
    { value: "98", suffix: "%", label: "Határidőre Kész", sublabel: "kötbérrel garantálva", icon: <Clock size={28} weight="duotone" />, accent: "from-cyan-300 to-sky-500" },
  ];

  return (
    <section className="stats-section-dark relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0L40 20L20 40L0 20Z' fill='%23ffffff' fill-opacity='0.4'/%3E%3C/svg%3E")`,
        backgroundSize: '40px 40px'
      }} />
      {/* Floating glow orbs */}
      <div className="absolute top-0 left-1/4 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-teal-500/10 rounded-full blur-3xl" />
      
      <div className="container-premium relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {stats.map((stat, index) => (
            <SectionReveal key={index} delay={index * 0.12}>
              <motion.div 
                className="stats-glass-card group"
                whileHover={{ y: -6, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                data-testid={`stat-card-${index}`}
              >
                {/* Accent top line */}
                <div className={`h-1 w-full bg-gradient-to-r ${stat.accent} rounded-t-2xl`} />
                
                <div className="p-5 md:p-7">
                  {/* Icon */}
                  <motion.div 
                    className={`w-11 h-11 md:w-13 md:h-13 rounded-xl bg-gradient-to-br ${stat.accent} flex items-center justify-center text-white mb-4 shadow-lg`}
                    whileHover={{ rotate: 8, scale: 1.1 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    {stat.icon}
                  </motion.div>
                  
                  {/* Number */}
                  <div className="mb-1">
                    <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                  </div>
                  
                  {/* Label */}
                  <div className="text-white/90 font-semibold text-sm md:text-base tracking-wide" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {stat.label}
                  </div>
                  <div className="text-cyan-300/60 text-xs mt-1 tracking-wide">
                    {stat.sublabel}
                  </div>
                </div>
              </motion.div>
            </SectionReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// Problem/Solution Section
const ProblemSolutionSection = () => {
  const problems = [
    { icon: <CurrencyCircleDollar size={24} />, text: "Rejtett költségek és utólagos felárak" },
    { icon: <Clock size={24} />, text: "Csúszó határidők, elhúzódó felújítás" },
    { icon: <Users size={24} />, text: "Koordinálatlan alvállalkozók" },
    { icon: <Shield size={24} />, text: "Garancia nélküli, szavatossági problémák" },
  ];

  const solutions = [
    { icon: <FileText size={24} />, text: "Tételes, fix árajánlat — módosítás nélkül" },
    { icon: <CalendarCheck size={24} />, text: "Szerződéses határidő kötbérrel" },
    { icon: <HardHat size={24} />, text: "32 fős saját csapat, egy felelős" },
    { icon: <Shield size={24} />, text: "5 év teljes körű garancia" },
  ];

  return (
    <section id="rolunk" className="section-green-light leaf-pattern">
      <div className="container-premium">
        <SectionReveal>
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="eco-badge mx-auto mb-4">
              <Target size={18} weight="fill" />
              Miért más a Báza-Bau?
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-cyan-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Az építkezés nem állíthatja meg<br />
              <span className="text-gradient-green">az otthonát</span>
            </h2>
          </div>
        </SectionReveal>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Problems */}
          <SectionReveal delay={0.2}>
            <div className="problem-card h-full">
              <h3 className="text-xl font-bold text-red-700 mb-8 flex items-center gap-3">
                <X size={24} weight="bold" className="text-red-500" />
                Amitől a vállalkozók tartanak
              </h3>
              <div className="space-y-5">
                {problems.map((problem, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center text-red-500 flex-shrink-0">
                      {problem.icon}
                    </div>
                    <span className="text-gray-700 font-medium">{problem.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </SectionReveal>

          {/* Solutions */}
          <SectionReveal delay={0.3}>
            <div className="solution-card h-full">
              <h3 className="text-xl font-bold text-cyan-700 mb-8 flex items-center gap-3">
                <CheckCircle size={24} weight="fill" className="text-cyan-600" />
                Amit a Báza-Bau garantál
              </h3>
              <div className="space-y-5">
                {solutions.map((solution, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-cyan-200 rounded-xl flex items-center justify-center text-cyan-700 flex-shrink-0">
                      {solution.icon}
                    </div>
                    <span className="text-gray-700 font-medium">{solution.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </SectionReveal>
        </div>
      </div>
    </section>
  );
};

// Services Section - Redesigned with Bento Grid
const ServicesSection = () => {
  const [activeService, setActiveService] = useState(0);
  
  const services = [
    {
      icon: <House size={36} weight="duotone" />,
      title: "Kulcsrakész Házépítés",
      description: "Családi házak, ikerházak, sorházak tervezése és kulcsrakész kivitelezése az alapoktól a beköltözésig.",
      badge: "Kulcsrakész",
      features: ["Családi ház", "Ikerház", "Sorház"],
      image: "https://images.pexels.com/photos/27938318/pexels-photo-27938318.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      color: "from-cyan-500 to-sky-600"
    },
    {
      icon: <Wrench size={36} weight="duotone" />,
      title: "Lakásfelújítás & Átalakítás",
      description: "Teljes körű lakás- és házfelújítás, térátalakítás, fürdőszoba és konyha korszerűsítés.",
      badge: "Népszerű",
      features: ["Lakásfelújítás", "Fürdőszoba", "Konyha"],
      image: "https://images.unsplash.com/photo-1770816307742-5e6d3d778838?w=800&q=80",
      color: "from-sky-500 to-cyan-600"
    },
    {
      icon: <Wall size={36} weight="duotone" />,
      title: "Tetőfelújítás & Tetőcsere",
      description: "Komplett tetőcsere, tetőjavítás, cserepezés, bádogos munka és tetőszigetelés.",
      badge: "Szakértő",
      features: ["Tetőcsere", "Cserepezés", "Bádogos munka"],
      image: "https://images.pexels.com/photos/31771166/pexels-photo-31771166.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      color: "from-cyan-600 to-sky-500"
    },
    {
      icon: <Buildings size={36} weight="duotone" />,
      title: "Bővítés & Hozzáépítés",
      description: "Meglévő otthonok bővítése, emeletráépítés, garázs, terasz és melléképületek.",
      badge: "Teljes körű",
      features: ["Emeletráépítés", "Garázs", "Terasz"],
      image: "https://images.unsplash.com/photo-1656829481918-398458ba16ce?w=800&q=80",
      color: "from-sky-600 to-cyan-500"
    },
    {
      icon: <PaintBrush size={36} weight="duotone" />,
      title: "Belsőépítészet & Burkolás",
      description: "Komplett belsőépítészet, burkolás, festés, gipszkartonozás, parkettázás — egy kivitelezőtől.",
      badge: "Komplex",
      features: ["Burkolás", "Festés", "Gipszkarton"],
      image: "https://images.unsplash.com/photo-1680781336783-8382d382e892?w=800&q=80",
      color: "from-cyan-500 to-sky-600"
    },
    {
      icon: <Shield size={36} weight="duotone" />,
      title: "Szigetelés & Energetika",
      description: "Épület-energetikai korszerűsítés, homlokzati szigetelés, nyílászáró csere.",
      badge: "Megtakarítás",
      features: ["Hőszigetelés", "Homlokzat", "Nyílászáró csere"],
      image: "https://images.unsplash.com/photo-1589572394490-771aa22db633?w=800&q=80",
      color: "from-sky-500 to-cyan-600"
    }
  ];

  return (
    <section id="szolgaltatasok" className="section-white overflow-hidden">
      <div className="container-premium">
        <SectionReveal>
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="eco-badge mx-auto mb-4">
              <Wrench size={18} weight="fill" />
              Szolgáltatásaink
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-cyan-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Minden építési igényre<br />
              <span className="text-gradient-green">profi megoldás</span>
            </h2>
          </div>
        </SectionReveal>

        {/* Bento Grid Layout */}
        <div className="grid lg:grid-cols-3 gap-4 md:gap-6" style={{ minWidth: 0 }}>
          {/* Left Column - Service List - horizontal scroll on mobile */}
          <div className="lg:col-span-1 order-2 lg:order-1 min-w-0 overflow-hidden">
            <div className="flex lg:flex-col gap-2 md:gap-3 overflow-x-auto lg:overflow-visible pb-4 lg:pb-0 scrollbar-hide">
              {services.map((service, index) => (
                <motion.button
                  key={index}
                  onClick={() => setActiveService(index)}
                  className={`flex-shrink-0 lg:flex-shrink lg:w-full text-left p-3 md:p-4 rounded-xl transition-all duration-300 flex items-center gap-3 md:gap-4 ${
                    activeService === index 
                      ? 'bg-cyan-600 text-white shadow-lg' 
                      : 'bg-white border border-cyan-100 text-cyan-800 hover:border-cyan-300 hover:shadow-md'
                  }`}
                  whileTap={{ scale: 0.98 }}
                  data-testid={`service-tab-${index}`}
                >
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center transition-all ${
                    activeService === index 
                      ? 'bg-white/20 text-white' 
                      : 'bg-cyan-100 text-cyan-600'
                  }`}>
                    {React.cloneElement(service.icon, { size: 24 })}
                  </div>
                  <div className="flex-grow min-w-0">
                    <h3 className="font-semibold text-xs md:text-sm truncate">{service.title}</h3>
                    <span className={`text-xs ${activeService === index ? 'text-cyan-200' : 'text-cyan-500'} hidden md:block`}>
                      {service.badge}
                    </span>
                  </div>
                  <CaretRight 
                    size={16} 
                    weight="bold" 
                    className={`transition-transform hidden lg:block ${activeService === index ? 'rotate-90' : ''}`}
                  />
                </motion.button>
              ))}
            </div>
          </div>

          {/* Right Column - Active Service Detail */}
          <div className="lg:col-span-2 order-1 lg:order-2 min-w-0 overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeService}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-2xl md:rounded-3xl border border-cyan-100 overflow-hidden shadow-xl"
              >
                {/* Image */}
                <div className="relative h-48 md:h-64 overflow-hidden">
                  <motion.img 
                    src={services[activeService].image} 
                    alt={services[activeService].title}
                    className="w-full h-full object-cover"
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.5 }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  
                  {/* Badge on Image */}
                  <div className="absolute top-4 left-4">
                    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-semibold bg-gradient-to-r ${services[activeService].color}`}>
                      <Star size={16} weight="fill" />
                      {services[activeService].badge}
                    </span>
                  </div>
                  
                  {/* Title on Image */}
                  <div className="absolute bottom-4 left-6 right-6">
                    <h3 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {services[activeService].title}
                    </h3>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 md:p-8">
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-6 line-clamp-3 md:line-clamp-none">
                    {services[activeService].description}
                  </p>

                  {/* Features */}
                  <div className="flex flex-wrap gap-2 md:gap-3 mb-6">
                    {services[activeService].features.map((feature, idx) => (
                      <motion.span
                        key={idx}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: idx * 0.1 }}
                        className="flex items-center gap-1.5 md:gap-2 bg-cyan-50 text-cyan-700 px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium"
                      >
                        <CheckCircle size={16} weight="fill" />
                        {feature}
                      </motion.span>
                    ))}
                  </div>

                  {/* CTA */}
                  <a href="#ajanlatkerem">
                    <Button className="btn-green">
                      Ajánlatot kérek
                      <ArrowRight weight="bold" className="ml-2" size={18} />
                    </Button>
                  </a>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

// USP Section - Interactive Smart Version
const USPSection = () => {
  const [expandedCard, setExpandedCard] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [showSavings, setShowSavings] = useState(false);
  const [satisfactionCount, setSatisfactionCount] = useState(150);

  // Simulate live satisfaction counter
  useEffect(() => {
    const interval = setInterval(() => {
      if (Math.random() > 0.7) {
        setSatisfactionCount(prev => prev + 1);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const usps = [
    { 
      icon: <Clock size={32} weight="duotone" />, 
      title: "Kötbéres Határidő", 
      desc: "Ha csúszunk, mi fizetünk — szerződésben",
      details: "Minden nap késésért napi kötbért vállalunk szerződésben. Egy család nem engedheti meg, hogy a költözés heteket csússzon — nálunk nem fog.",
      stat: "98%",
      statLabel: "Határidőre kész",
      savings: { amount: "0 nap", desc: "Csúszás miatti bevételkiesés" },
      color: "from-sky-500 to-cyan-600"
    },
    { 
      icon: <Shield size={32} weight="duotone" />, 
      title: "Üzemmenet Melletti Kivitelezés", 
      desc: "Dolgozunk, miközben Ön otthon van",
      details: "Lakásfelújítás, házátalakítás? Úgy szervezzük a munkát, hogy a mindennapi élete zavartalanul folytatódhasson. Szakaszolás, hétvégi munka, zajcsökkentés — megoldjuk.",
      stat: "0%",
      statLabel: "Üzemszünet",
      savings: { amount: "Folyamatos", desc: "Bevétel a felújítás alatt is" },
      color: "from-cyan-500 to-sky-600"
    },
    { 
      icon: <FileText size={32} weight="duotone" />, 
      title: "Fix Áras Szerződés", 
      desc: "Amiben megegyeztünk, annyi — rejtett költségek nélkül",
      details: "Részletes, tételes árajánlat, fix végösszeg. Nincs 'előre nem látható költség' meglepetés. Tervezhető büdzsé, átlátható számlázás — ahogy egy családnak kell.",
      stat: "0 Ft",
      statLabel: "Rejtett költség",
      savings: { amount: "10-15%", desc: "Megspórolt utólagos felár" },
      color: "from-teal-500 to-cyan-600"
    },
    { 
      icon: <Ruler size={32} weight="duotone" />, 
      title: "Komplex Engedélyeztetés", 
      desc: "Intézzük az összes papírmunkát Ön helyett",
      details: "Építési engedély, tervrajz, statika, hatósági egyeztetés — mindet mi intézzük. Ön csak a kulcsot veszi át a kész otthonnál.",
      stat: "100%",
      statLabel: "Ügyintézés",
      savings: { amount: "Hónapok", desc: "Megspórolt ügyintézési idő" },
      color: "from-cyan-600 to-teal-500"
    },
    { 
      icon: <Users size={32} weight="duotone" />, 
      title: "Dedikált Projektmenedzser", 
      desc: "Egy ember, aki mindenre válaszol",
      details: "Minden projekthez saját projektmenedzsert rendelünk, aki az Ön egyetlen kapcsolattartója. Nem kell 5 alvállalkozót hívogatnia — egy telefonhívás, és minden megoldódik.",
      stat: "1",
      statLabel: "Kapcsolattartó",
      savings: { amount: "Egyszerűbb", desc: "Kommunikáció, kevesebb stressz" },
      color: "from-sky-600 to-cyan-500"
    },
    { 
      icon: <Camera size={32} weight="duotone" />, 
      title: "Heti Fotós Riport", 
      desc: "Kövesse a projektet anélkül, hogy hazamenne a munkából",
      details: "Minden héten részletes képes beszámolót kap e-mailben. Akár a munkahelyéről, akár nyaralásról is nyomon követheti, hol tart az otthona.",
      stat: "52",
      statLabel: "Riport évente",
      savings: { amount: "Átláthatóság", desc: "Teljes kontroll a távolból is" },
      color: "from-cyan-500 to-sky-500"
    }
  ];

  const toggleCard = (index) => {
    setExpandedCard(expandedCard === index ? null : index);
  };

  return (
    <section className="section-green-light overflow-hidden">
      <div className="container-premium">
        <SectionReveal>
          <div className="text-center max-w-2xl mx-auto mb-8">
            <div className="eco-badge mx-auto mb-4">
              <Trophy size={18} weight="fill" />
              Miért válasszon minket?
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-cyan-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              6 ok, amiért<br />
              <span className="text-gradient-green">mások vagyunk</span>
            </h2>
          </div>
        </SectionReveal>

        {/* Live Satisfaction Counter */}
        <SectionReveal delay={0.1}>
          <div className="flex justify-center mb-8 md:mb-12">
            <motion.div 
              className="bg-white rounded-full px-4 py-2 md:px-6 md:py-3 shadow-lg border border-cyan-100 flex items-center gap-2 md:gap-4"
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5 md:h-3 md:w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 md:h-3 md:w-3 bg-cyan-500"></span>
                </span>
                <span className="text-xs md:text-sm text-gray-600">Elégedett ügyfelek:</span>
              </div>
              <motion.span 
                key={satisfactionCount}
                initial={{ scale: 1.2, color: '#22C55E' }}
                animate={{ scale: 1, color: '#15803D' }}
                className="text-xl md:text-2xl font-bold text-cyan-700"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {satisfactionCount}+
              </motion.span>
            </motion.div>
          </div>
        </SectionReveal>

        {/* Savings Mode Toggle */}
        <SectionReveal delay={0.15}>
          <div className="flex justify-center mb-8 md:mb-10">
            <button
              onClick={() => setShowSavings(!showSavings)}
              className={`flex items-center gap-2 px-4 py-2.5 md:px-6 md:py-3 rounded-full text-sm md:text-base font-medium transition-all duration-300 ${
                showSavings 
                  ? 'bg-cyan-600 text-white shadow-lg' 
                  : 'bg-white text-cyan-700 border-2 border-cyan-200 hover:border-cyan-400'
              }`}
              data-testid="savings-toggle"
            >
              <CurrencyCircleDollar size={18} />
              {showSavings ? 'Megtakarítások láthatóak' : 'Mutasd a megtakarításokat'}
            </button>
          </div>
        </SectionReveal>

        {/* USP Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {usps.map((usp, index) => (
            <SectionReveal key={index} delay={index * 0.08}>
              <motion.div 
                className={`relative bg-white rounded-2xl border-2 transition-all duration-500 cursor-pointer overflow-hidden ${
                  expandedCard === index 
                    ? 'border-cyan-400 shadow-2xl' 
                    : 'border-cyan-100 hover:border-cyan-300 hover:shadow-xl'
                }`}
                onClick={() => toggleCard(index)}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
                layout
                data-testid={`usp-card-${index}`}
              >
                {/* Gradient top bar */}
                <motion.div 
                  className={`h-1 bg-gradient-to-r ${usp.color}`}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: hoveredCard === index || expandedCard === index ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                  style={{ transformOrigin: 'left' }}
                />

                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <motion.div 
                      className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${usp.color} flex items-center justify-center text-white shadow-lg`}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      {usp.icon}
                    </motion.div>
                    
                    {/* Quick Stat Badge */}
                    <motion.div 
                      className="text-right"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      <div className="text-2xl font-bold text-cyan-600" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {usp.stat}
                      </div>
                      <div className="text-xs text-gray-500">{usp.statLabel}</div>
                    </motion.div>
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-xl font-bold text-cyan-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {usp.title}
                  </h3>
                  <p className="text-gray-500 text-sm mb-4">{usp.desc}</p>

                  {/* Savings Badge - Always visible when toggled */}
                  <AnimatePresence>
                    {showSavings && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="bg-gradient-to-r from-cyan-50 to-sky-50 border border-cyan-200 rounded-xl p-3 mb-4"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center">
                            <CurrencyCircleDollar size={18} className="text-white" weight="fill" />
                          </div>
                          <div>
                            <div className="text-lg font-bold text-cyan-700" style={{ fontFamily: "'Outfit', sans-serif" }}>
                              {usp.savings.amount}
                            </div>
                            <div className="text-xs text-cyan-600">{usp.savings.desc}</div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Expanded Content */}
                  <AnimatePresence>
                    {expandedCard === index && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="pt-4 border-t border-cyan-100">
                          <p className="text-gray-600 text-sm leading-relaxed">
                            {usp.details}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Expand Indicator */}
                  <div className="flex items-center justify-center mt-4">
                    <motion.div
                      animate={{ rotate: expandedCard === index ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="text-cyan-400"
                    >
                      <CaretRight size={20} weight="bold" style={{ transform: 'rotate(90deg)' }} />
                    </motion.div>
                  </div>
                </div>

                {/* Hover Glow Effect */}
                <motion.div
                  className={`absolute inset-0 bg-gradient-to-br ${usp.color} opacity-0 pointer-events-none`}
                  animate={{ opacity: hoveredCard === index ? 0.03 : 0 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.div>
            </SectionReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// References Section
const ReferencesSection = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  
  const references = [
    {
      id: 1,
      category: "uj-epites",
      title: "Családi Ház Építés",
      location: "Budaörs",
      image: IMAGES.portfolio1,
      size: "1200 m²",
      duration: "6 hónap"
    },
    {
      id: 2,
      category: "felujitas",
      title: "Lakásfelújítás",
      location: "Budapest, XI. ker.",
      image: IMAGES.portfolio2,
      size: "450 m²",
      duration: "3 hónap"
    },
    {
      id: 3,
      category: "bovites",
      title: "Tetőcsere & Bővítés",
      location: "Szentendre",
      image: IMAGES.portfolio3,
      size: "800 m²",
      duration: "4 hónap"
    },
    {
      id: 4,
      category: "uj-epites",
      title: "Ikerház Építés",
      location: "Érd",
      image: IMAGES.portfolio4,
      size: "2400 m²",
      duration: "10 hónap"
    },
  ];

  const filters = [
    { id: "all", label: "Összes" },
    { id: "uj-epites", label: "Új Építés" },
    { id: "felujitas", label: "Felújítás" },
    { id: "bovites", label: "Bővítés" },
  ];

  const filteredReferences = activeFilter === "all" 
    ? references 
    : references.filter(r => r.category === activeFilter);

  return (
    <section id="referenciak" className="section-white">
      <div className="container-premium">
        <SectionReveal>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="eco-badge mx-auto mb-4">
              <Camera size={18} weight="fill" />
              Referenciák
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-cyan-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Amit építettünk,<br />
              <span className="text-gradient-green">az áll</span>
            </h2>
          </div>
        </SectionReveal>

        {/* Filters */}
        <SectionReveal delay={0.2}>
          <div className="flex justify-center gap-2 mb-12">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`portfolio-filter-btn ${activeFilter === filter.id ? "active" : ""}`}
                data-testid={`filter-${filter.id}`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </SectionReveal>

        {/* Grid */}
        <AnimatePresence mode="wait">
          <motion.div 
            key={activeFilter}
            className="grid md:grid-cols-2 gap-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {filteredReferences.map((ref, index) => (
              <SectionReveal key={ref.id} delay={index * 0.1}>
                <div className="reference-card-eco" data-testid={`reference-card-${index}`}>
                  <div className="image-container">
                    <img src={ref.image} alt={ref.title} />
                    <div className="category-badge">
                      {filters.find(f => f.id === ref.category)?.label}
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
                      <MapPin size={14} />
                      {ref.location}
                    </div>
                    <h3 className="text-xl font-bold text-cyan-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {ref.title}
                    </h3>
                    <div className="flex gap-6 text-sm">
                      <div>
                        <span className="text-gray-500">Méret: </span>
                        <span className="text-cyan-800 font-semibold">{ref.size}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Időtartam: </span>
                        <span className="text-cyan-800 font-semibold">{ref.duration}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

// Process Section
const ProcessSection = () => {
  const steps = [
    { num: "01", title: "Konzultáció", desc: "Ingyenes helyszíni felmérés" },
    { num: "02", title: "Tervezés", desc: "Műszaki terv & engedélyezés" },
    { num: "03", title: "Árajánlat", desc: "Tételes, fix árajánlat" },
    { num: "04", title: "Kivitelezés", desc: "32 fős saját csapattal" },
    { num: "05", title: "Átadás", desc: "5 év garanciával" },
  ];

  return (
    <section id="folyamat" className="section-green-dark text-white">
      <div className="container-premium">
        <SectionReveal>
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-flex items-center gap-2 text-cyan-300 text-sm font-semibold uppercase tracking-wider mb-4">
              <Target size={18} weight="fill" />
              Hogyan dolgozunk?
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              5 lépés a<br />
              <span className="text-cyan-400">kulcsrakész átadásig</span>
            </h2>
          </div>
        </SectionReveal>

        <div className="flex flex-col md:flex-row justify-between relative gap-2 md:gap-0">
          {/* Timeline line for desktop */}
          <div className="hidden md:block absolute top-7 left-[10%] right-[10%] h-[3px] bg-gradient-to-r from-cyan-400 via-cyan-500/50 to-cyan-300/20" />
          
          {/* Vertical line for mobile */}
          <div className="md:hidden absolute top-0 bottom-0 left-7 w-[3px] bg-gradient-to-b from-cyan-400 via-cyan-500/50 to-cyan-300/20" />
          
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="flex md:flex-col items-center md:text-center gap-5 md:gap-0 py-3 md:py-0 md:mb-0 relative"
              data-testid={`process-step-${index}`}
            >
              <div className="process-dot flex-shrink-0 relative z-10">
                {step.num}
              </div>
              <div>
                <h3 className="text-lg font-bold mb-1 md:mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {step.title}
                </h3>
                <p className="text-cyan-200 text-sm md:max-w-[150px] md:mx-auto">{step.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "Tóth Gábor",
      role: "Ügyvezető, Logisztikai Kft.",
      text: "A házunk 6 hónap alatt készült el, pontosan a megbeszélt áron. A heti fotós riportok és a dedikált projektvezetés kiemelkedő volt.",
      rating: 5
    },
    {
      name: "Horváth Katalin",
      role: "Irodavezető, IT cég",
      text: "A lakásfelújítást úgy oldották meg, hogy a mindennapi életünket minimálisan zavarta. Profi koordináció, kiváló minőség — ajánlom mindenkinek.",
      rating: 5
    },
    {
      name: "Varga István",
      role: "Tulajdonos, Gyártó Kft.",
      text: "3 ajánlatot kértünk, a Báza-Bau volt a legátláthatóbb. A tételes árajánlatból tudtuk, pontosan mire költünk. Azóta is velük dolgozunk.",
      rating: 5
    },
  ];

  return (
    <section className="section-white">
      <div className="container-premium">
        <SectionReveal>
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="eco-badge mx-auto mb-4">
              <Star size={18} weight="fill" />
              Vélemények
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-cyan-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Amit ügyfeleink<br />
              <span className="text-gradient-green">mondanak</span>
            </h2>
          </div>
        </SectionReveal>

        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <SectionReveal key={index} delay={index * 0.1}>
              <div className="testimonial-card-eco h-full flex flex-col">
                <Quotes size={40} className="text-cyan-200 mb-4" weight="fill" />
                <p className="text-gray-600 mb-6 leading-relaxed flex-grow">{testimonial.text}</p>
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={18} className="text-cyan-500" weight="fill" />
                  ))}
                </div>
                <div className="border-t border-cyan-100 pt-4">
                  <div className="font-bold text-cyan-900">{testimonial.name}</div>
                  <div className="text-gray-500 text-sm">{testimonial.role}</div>
                </div>
              </div>
            </SectionReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// Quote Form Section - 6-Step Questionnaire
const QuoteFormSection = () => {
  const [step, setStep] = useState(1);
  const totalSteps = 6;
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    services: [],
    custom_service: "",
    area_size: "",
    property_type: "",
    timeline: "",
    city: "",
    zip_code: "",
    address: "",
    project_description: "",
    name: "",
    phone: "",
    email: "",
  });

  const serviceOptions = [
    { id: "hazepites", label: "Kulcsrakész Házépítés", icon: <House size={22} weight="duotone" /> },
    { id: "felujitas", label: "Lakásfelújítás & Átalakítás", icon: <Wrench size={22} weight="duotone" /> },
    { id: "tetofelujitas", label: "Tetőfelújítás & Tetőcsere", icon: <Wall size={22} weight="duotone" /> },
    { id: "bovites", label: "Bővítés & Hozzáépítés", icon: <Buildings size={22} weight="duotone" /> },
    { id: "belsoepiteszet", label: "Belsőépítészet & Burkolás", icon: <PaintBrush size={22} weight="duotone" /> },
    { id: "energia", label: "Szigetelés & Energetika", icon: <Shield size={22} weight="duotone" /> },
  ];

  const areaSizeOptions = [
    { id: "small", label: "Kis munka (100 m² alatt)" },
    { id: "medium", label: "Közepes (100–500 m²)" },
    { id: "large", label: "Nagy (500 m² felett)" },
  ];

  const propertyOptions = [
    { id: "haz", label: "Családi ház", icon: <House size={22} weight="duotone" /> },
    { id: "lakas", label: "Lakás / társasház", icon: <Buildings size={22} weight="duotone" /> },
    { id: "nyaralo", label: "Nyaraló / vidéki ház", icon: <House size={22} weight="duotone" /> },
    { id: "telek", label: "Üres telek (új építés)", icon: <HardHat size={22} weight="duotone" /> },
    { id: "egyeb", label: "Egyéb", icon: <HardHat size={22} weight="duotone" /> },
  ];

  const timelineOptions = [
    { id: "urgent", label: "Azonnal, vagy 1-2 héten belül" },
    { id: "2-4-weeks", label: "2-4 héten belül" },
    { id: "1-2-month", label: "1-2 hónapon belül" },
  ];

  const stepLabels = ["Szolgáltatás", "Terület", "Ingatlan", "Időzítés", "Részletek", "Elérhetőség"];

  const toggleService = (id) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(id)
        ? prev.services.filter(s => s !== id)
        : [...prev.services, id]
    }));
  };

  const canProceed = () => {
    switch(step) {
      case 1: return formData.services.length > 0 || formData.custom_service.trim() !== "";
      case 2: return formData.area_size !== "";
      case 3: return formData.property_type !== "";
      case 4: return formData.timeline !== "";
      case 5: return formData.project_description.trim() !== "" && formData.city.trim() !== "";
      case 6: return formData.name.trim() !== "" && formData.phone.trim() !== "";
      default: return false;
    }
  };

  const serviceLabels = {
    "hazepites": "Kulcsrakész Házépítés",
    "felujitas": "Lakásfelújítás & Átalakítás",
    "tetofelujitas": "Tetőfelújítás & Tetőcsere",
    "bovites": "Bővítés & Hozzáépítés",
    "belsoepiteszet": "Belsőépítészet & Burkolás",
    "energia": "Szigetelés & Energetika",
  };
  const areaLabels = { small: "Kis munka (100 m² alatt)", medium: "Közepes (100–500 m²)", large: "Nagy (500 m² felett)" };
  const propertyLabels = { haz: "Családi ház", lakas: "Lakás / társasház", nyaralo: "Nyaraló / vidéki ház", telek: "Üres telek (új építés)", egyeb: "Egyéb" };
  const timelineLabels = { urgent: "Azonnal, vagy 1-2 héten belül", "2-4-weeks": "2-4 héten belül", "1-2-month": "1-2 hónapon belül" };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const emailData = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email || "Nem adott meg",
        services: formData.services.map(s => serviceLabels[s] || s).join(", ") || "Nincs kiválasztva",
        custom_service: formData.custom_service || "Nincs",
        area_size: areaLabels[formData.area_size] || formData.area_size || "Nem adta meg",
        property_type: propertyLabels[formData.property_type] || formData.property_type || "Nem adta meg",
        timeline: timelineLabels[formData.timeline] || formData.timeline || "Nem adta meg",
        city: formData.city || "Nem adta meg",
        zip_code: formData.zip_code || "Nem adta meg",
        address: formData.address || "Nem adta meg",
        project_description: formData.project_description || "Nem adta meg",
      };

      // Backend mentés mindig
      await axios.post(`${API}/quote-request`, formData);
      // EmailJS küldés külön (ha nem sikerül, a mentés akkor is megvan)
      try {
        await emailjs.send("service_bizxbg7", "template_an3aroj", emailData, "6A-DsULafdeZHhGG6");
      } catch (emailErr) {
        console.error("EmailJS hiba:", emailErr);
      }
      setSubmitted(true);
      toast.success("Köszönjük! Hamarosan felvesszük Önnel a kapcsolatot!");
    } catch (error) {
      toast.error("Hiba történt. Kérjük, próbálja újra!");
    } finally {
      setLoading(false);
    }
  };

  const progressPercent = (step / totalSteps) * 100;

  if (submitted) {
    return (
      <section id="ajanlatkerem" className="quote-section-highlight relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-[100px] animate-glow-drift-1" />
        <div className="container-premium relative z-10">
          <motion.div
            className="max-w-xl mx-auto text-center py-16"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="w-20 h-20 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={48} weight="fill" className="text-cyan-300" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Igényfelmérés elküldve!
            </h2>
            <p className="text-cyan-100/80 text-lg mb-6">
              Köszönjük! 24 órán belül visszahívjuk, és egyeztetjük a díjmentes helyszíni felmérés időpontját.
            </p>
            <Button
              className="btn-green"
              onClick={() => { setSubmitted(false); setStep(1); setFormData({ services: [], custom_service: "", area_size: "", property_type: "", timeline: "", city: "", zip_code: "", address: "", project_description: "", name: "", phone: "", email: "" }); }}
              data-testid="questionnaire-new"
            >
              Új igényfelmérés
            </Button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="ajanlatkerem" className="quote-section-highlight relative overflow-hidden">
      {/* Animated glow orbs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-[100px] animate-glow-drift-1" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-sky-400/10 rounded-full blur-[80px] animate-glow-drift-2" />
      <div className="absolute top-1/2 left-0 w-64 h-64 bg-teal-500/8 rounded-full blur-[60px] animate-glow-drift-3" />
      
      <div className="container-premium relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left side - Info */}
          <SectionReveal>
            <div className="eco-badge-light mb-4">
              <Envelope size={18} weight="fill" />
              Igényfelmérés
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Kérjen<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-sky-300">ajánlatot</span>
            </h2>
            <p className="text-cyan-100/80 text-lg mb-10 leading-relaxed">
              Töltse ki az igényfelmérést, és <strong className="text-white">24 órán belül visszahívjuk</strong>. A helyszíni felmérés és az árajánlat <strong className="text-white">díjmentes</strong>, kötelezettség nélkül.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10 flex items-center justify-center text-cyan-300">
                  <Phone size={24} />
                </div>
                <div>
                  <div className="text-sm text-cyan-300/60">Telefonszám</div>
                  <div className="font-semibold text-white">+36 70 260 2689</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10 flex items-center justify-center text-cyan-300">
                  <Envelope size={24} />
                </div>
                <div>
                  <div className="text-sm text-cyan-300/60">Email</div>
                  <div className="font-semibold text-white">baza.bau.kft@gmail.com</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10 flex items-center justify-center text-cyan-300">
                  <MapPin size={24} />
                </div>
                <div>
                  <div className="text-sm text-cyan-300/60">Szolgáltatási terület</div>
                  <div className="font-semibold text-white">Egész Magyarország</div>
                </div>
              </div>
            </div>

            <div className="mt-10 p-5 bg-white/5 backdrop-blur-sm border-l-4 border-cyan-400 rounded-r-xl">
              <p className="text-cyan-100/80 text-sm">
                <span className="font-bold text-cyan-300">Ne várjon!</span> A halogatás csak növeli a felújítási költségeket. Kérjen ingyenes felmérést most!
              </p>
            </div>
          </SectionReveal>

          {/* Right side - Questionnaire with LED glow */}
          <SectionReveal delay={0.2}>
            <div className="quote-form-glow-wrapper" data-testid="questionnaire-glow">
              <div className="quote-form-container-dark" data-testid="questionnaire-container">
              {/* Step indicator */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500 uppercase tracking-widest font-semibold">
                  {step}. lépés / {totalSteps}
                </span>
                <span className="text-sm font-semibold text-cyan-600">{stepLabels[step - 1]}</span>
              </div>
              {/* Progress bar */}
              <div className="h-1 bg-cyan-100 rounded-full mb-8">
                <motion.div
                  className="h-full bg-gradient-to-r from-cyan-500 to-sky-500 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercent}%` }}
                  transition={{ duration: 0.4 }}
                />
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Step 1: Services */}
                  {step === 1 && (
                    <div>
                      <h3 className="text-xl font-bold text-cyan-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        Miben tudunk segíteni?
                      </h3>
                      <p className="text-gray-500 text-sm mb-6">Válasszon egyet vagy többet</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                        {serviceOptions.map((svc) => (
                          <button
                            key={svc.id}
                            type="button"
                            onClick={() => toggleService(svc.id)}
                            className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                              formData.services.includes(svc.id)
                                ? "border-cyan-500 bg-cyan-50 text-cyan-800"
                                : "border-cyan-100 bg-white text-gray-700 hover:border-cyan-300"
                            }`}
                            data-testid={`service-option-${svc.id}`}
                          >
                            <span className={formData.services.includes(svc.id) ? "text-cyan-600" : "text-gray-400"}>{svc.icon}</span>
                            <span className="font-medium text-sm">{svc.label}</span>
                          </button>
                        ))}
                      </div>
                      <div>
                        <label className="flex items-center gap-2 text-xs text-gray-500 uppercase tracking-wider font-semibold mb-2">
                          <ChatCircleDots size={16} /> Egyedi igény
                        </label>
                        <Input
                          value={formData.custom_service}
                          onChange={(e) => setFormData({ ...formData, custom_service: e.target.value })}
                          placeholder="Pl.: Medence építés, kerítés..."
                          className="input-light"
                          data-testid="questionnaire-custom-service"
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 2: Area Size */}
                  {step === 2 && (
                    <div>
                      <h3 className="text-xl font-bold text-cyan-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        Mekkora a terület?
                      </h3>
                      <p className="text-gray-500 text-sm mb-6">Becsült alapterület</p>
                      <div className="space-y-3">
                        {areaSizeOptions.map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => setFormData({ ...formData, area_size: opt.id })}
                            className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                              formData.area_size === opt.id
                                ? "border-cyan-500 bg-cyan-50 text-cyan-800"
                                : "border-cyan-100 bg-white text-gray-700 hover:border-cyan-300"
                            }`}
                            data-testid={`area-option-${opt.id}`}
                          >
                            <Ruler size={20} className={formData.area_size === opt.id ? "text-cyan-600" : "text-gray-400"} />
                            <span className="font-medium">{opt.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 3: Property Type */}
                  {step === 3 && (
                    <div>
                      <h3 className="text-xl font-bold text-cyan-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        Milyen típusú az ingatlan?
                      </h3>
                      <p className="text-gray-500 text-sm mb-6">Válasszon egyet</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {propertyOptions.map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => setFormData({ ...formData, property_type: opt.id })}
                            className={`flex items-center gap-3 p-5 rounded-xl border-2 text-left transition-all duration-200 ${
                              formData.property_type === opt.id
                                ? "border-cyan-500 bg-cyan-50 text-cyan-800"
                                : "border-cyan-100 bg-white text-gray-700 hover:border-cyan-300"
                            }`}
                            data-testid={`property-option-${opt.id}`}
                          >
                            <span className={formData.property_type === opt.id ? "text-cyan-600" : "text-gray-400"}>{opt.icon}</span>
                            <span className="font-medium text-sm">{opt.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 4: Timeline */}
                  {step === 4 && (
                    <div>
                      <h3 className="text-xl font-bold text-cyan-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        Mikor szeretné elkezdeni?
                      </h3>
                      <p className="text-gray-500 text-sm mb-6">Tervezett indulás</p>
                      <div className="space-y-3">
                        {timelineOptions.map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => setFormData({ ...formData, timeline: opt.id })}
                            className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                              formData.timeline === opt.id
                                ? "border-cyan-500 bg-cyan-50 text-cyan-800"
                                : "border-cyan-100 bg-white text-gray-700 hover:border-cyan-300"
                            }`}
                            data-testid={`timeline-option-${opt.id}`}
                          >
                            <Clock size={20} className={formData.timeline === opt.id ? "text-cyan-600" : "text-gray-400"} />
                            <span className="font-medium">{opt.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 5: Details */}
                  {step === 5 && (
                    <div>
                      <h3 className="text-xl font-bold text-cyan-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        Részletek
                      </h3>
                      <p className="text-gray-500 text-sm mb-6">Minden információ segít a pontosabb ajánlatban</p>
                      <div className="space-y-5">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold text-cyan-800 mb-2">Város *</label>
                            <Input
                              value={formData.city}
                              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                              placeholder="Pl.: Budapest"
                              className="input-light"
                              data-testid="questionnaire-city"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-cyan-800 mb-2">Irányítószám</label>
                            <Input
                              value={formData.zip_code}
                              onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                              placeholder="Pl.: 1111"
                              className="input-light"
                              data-testid="questionnaire-zipcode"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-cyan-800 mb-2">Cím (utca, házszám)</label>
                          <Input
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            placeholder="Pl.: Kossuth Lajos utca 12."
                            className="input-light"
                            data-testid="questionnaire-address"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-cyan-800 mb-2">Projekt leírása *</label>
                          <Textarea
                            value={formData.project_description}
                            onChange={(e) => setFormData({ ...formData, project_description: e.target.value })}
                            placeholder="Írja le röviden, milyen munkát szeretne..."
                            rows={4}
                            className="input-light resize-none"
                            data-testid="questionnaire-description"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 6: Contact */}
                  {step === 6 && (
                    <div>
                      <h3 className="text-xl font-bold text-cyan-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        Elérhetőségei
                      </h3>
                      <p className="text-gray-500 text-sm mb-6">Hogy visszahívhassuk</p>
                      <div className="space-y-5">
                        <div>
                          <label className="block text-sm font-semibold text-cyan-800 mb-2">Név *</label>
                          <Input
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="Teljes név"
                            required
                            className="input-light"
                            data-testid="questionnaire-name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-cyan-800 mb-2">Telefonszám *</label>
                          <Input
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            placeholder="+36 XX XXX XXXX"
                            required
                            className="input-light"
                            data-testid="questionnaire-phone"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-cyan-800 mb-2">Email</label>
                          <Input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            placeholder="pelda@email.hu"
                            className="input-light"
                            data-testid="questionnaire-email"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Navigation buttons */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-cyan-100">
                <button
                  type="button"
                  onClick={() => setStep(Math.max(1, step - 1))}
                  className={`flex items-center gap-2 px-5 py-3 rounded-xl border-2 border-cyan-200 text-cyan-700 font-semibold text-sm transition-all hover:border-cyan-400 ${
                    step === 1 ? "opacity-40 pointer-events-none" : ""
                  }`}
                  data-testid="questionnaire-prev"
                >
                  <CaretRight size={16} weight="bold" className="rotate-180" /> Vissza
                </button>

                {step < totalSteps ? (
                  <button
                    type="button"
                    onClick={() => canProceed() && setStep(step + 1)}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
                      canProceed()
                        ? "bg-cyan-600 text-white hover:bg-cyan-700 shadow-lg shadow-cyan-200"
                        : "bg-cyan-100 text-cyan-400 cursor-not-allowed"
                    }`}
                    data-testid="questionnaire-next"
                  >
                    Következő <CaretRight size={16} weight="bold" />
                  </button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={loading || !canProceed()}
                    className="btn-green px-8"
                    data-testid="questionnaire-submit"
                  >
                    {loading ? "Küldés..." : "Igényfelmérés küldése"}
                    {!loading && <ArrowRight weight="bold" className="ml-2" size={18} />}
                  </Button>
                )}
              </div>

              <p className="text-xs text-gray-400 text-center mt-4">
                Az ajánlatkérés díjmentes és kötelezettségmentes.
              </p>
              </div>
            </div>
          </SectionReveal>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <footer 
      id="kapcsolat" 
      className="footer-eco py-16 md:py-20 relative overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Animated top strip on hover */}
      <motion.div 
        className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-400 via-sky-300 to-cyan-500"
        initial={{ scaleX: 0, opacity: 0 }}
        animate={{ 
          scaleX: isHovered ? 1 : 0, 
          opacity: isHovered ? 1 : 0 
        }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        style={{ transformOrigin: "left" }}
      />
      
      {/* Secondary animated glow strip */}
      <motion.div 
        className="absolute top-0 left-0 right-0 h-1"
        initial={{ opacity: 0 }}
        animate={{ opacity: isHovered ? 1 : 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        style={{
          background: "linear-gradient(90deg, transparent, rgba(134, 239, 172, 0.8), transparent)",
          filter: "blur(4px)"
        }}
      />
      
      <div className="container-premium relative z-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10 mb-12 md:mb-16">
          <div className="lg:col-span-2">
            <div className="inline-block bg-white rounded-2xl p-3 md:p-4 mb-6 shadow-lg">
              <img src="/logo-dark.png" alt="Báza-Bau Kft." className="h-12 md:h-16" />
            </div>
            <p className="text-cyan-200 max-w-md leading-relaxed mb-6 text-sm md:text-base">
              Prémium generálkivitelezés családoknak egész Magyarországon. 
              Megbízható kivitelezés, garantált határidők.
            </p>
            <div className="inline-flex items-center gap-2 bg-cyan-700 text-cyan-100 px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-semibold">
              <HardHat size={14} weight="fill" />
              Generálkivitelezés & Felújítás
            </div>
          </div>
        
        <div>
          <h4 className="font-bold text-white mb-4 md:mb-6 text-sm md:text-base" style={{ fontFamily: "'Outfit', sans-serif" }}>Szolgáltatások</h4>
          <ul className="space-y-2 md:space-y-3 text-cyan-200 text-sm">
            <li><a href="#szolgaltatasok" className="hover:text-white transition-colors">Kulcsrakész házépítés</a></li>
            <li><a href="#szolgaltatasok" className="hover:text-white transition-colors">Lakásfelújítás</a></li>
            <li><a href="#szolgaltatasok" className="hover:text-white transition-colors">Tetőfelújítás & tetőcsere</a></li>
            <li><a href="#szolgaltatasok" className="hover:text-white transition-colors">Bővítés</a></li>
            <li><a href="#szolgaltatasok" className="hover:text-white transition-colors">Belsőépítészet & burkolás</a></li>
          </ul>
        </div>
        
        <div>
          <h4 className="font-bold text-white mb-4 md:mb-6 text-sm md:text-base" style={{ fontFamily: "'Outfit', sans-serif" }}>Kapcsolat</h4>
          <ul className="space-y-3 md:space-y-4 text-cyan-200 text-sm">
            <li className="flex items-center gap-3">
              <MapPin size={16} className="text-cyan-400" />
              Egész Magyarország
            </li>
            <li className="flex items-center gap-3">
              <Phone size={16} className="text-cyan-400" />
              +36 70 260 2689
            </li>
            <li className="flex items-center gap-3">
              <Envelope size={16} className="text-cyan-400" />
              baza.bau.kft@gmail.com
            </li>
          </ul>
        </div>
      </div>
      
      <div className="border-t border-cyan-700 pt-6 md:pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-cyan-400 text-xs md:text-sm">
          © 2025 Báza-Bau Kft. Minden jog fenntartva.
        </p>
        <div className="flex gap-4 md:gap-6 text-cyan-400 text-xs md:text-sm">
          <Link to="/adatvedelem" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Adatvédelem</Link>
          <Link to="/cookie-szabalyzat" className="hover:text-white transition-colors" data-testid="footer-cookie-link">Cookie szabályzat</Link>
          <a href="#" className="hover:text-white transition-colors">ÁSZF</a>
        </div>
      </div>
    </div>
    
    {/* Watermark */}
    <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none">
      <div className="footer-brand-green text-center">BÁZA-BAU</div>
    </div>
  </footer>
  );
};

// Main Landing Page
const LandingPage = () => {
  return (
    <div className="min-h-screen bg-[#FAFDFB]" data-testid="landing-page">
      <ScrollProgressBar />
      <Header />
      <HeroSection />
      <StatsSection />
      <QuoteFormSection />
      <ProblemSolutionSection />
      <ServicesSection />
      <USPSection />
      <ReferencesSection />
      <ProcessSection />
      <TestimonialsSection />
      <QuoteFormSection />
      <Footer />
      <FloatingActions />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster 
        position="top-center" 
        richColors 
        toastOptions={{
          style: {
            background: '#ffffff',
            border: '1px solid #22C55E',
          },
        }}
      />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/adatvedelem" element={<PrivacyPolicy />} />
          <Route path="/cookie-szabalyzat" element={<CookiePolicy />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
